/**
 * The single keyboard binding table (ui-ux §6.25, §7.5; A38). Owner: UI/Visual (F3).
 *
 * Scopes: `global`, a screen id, or `overlay:<id>` for modal sheets (a modal overlay makes the page inert, so its digits
 * never compete with the tab keys). Rules (checked by {@link validateKeymap}, unit-tested):
 * - no duplicate `(scope, key)`;
 * - global keys `M`, `?`, `Escape`, `1`–`5`, `Alt+1…5` are reserved — a screen may not rebind them, except that the
 *   Поединок and the card match ({@link DIGIT_ACTION_SCREENS}) use `1`–`0` as actions (tabs move to `Alt+1…5` there);
 * - Miracle is `X` (R32), never `M`.
 *
 * Decision (doc conflict, ui-ux §6.25 vs its own reserved-key rule): the desktop Житие sections use `Shift+1…6`, not
 * bare digits, so the global tab digits stay intact on the hub.
 */
import type { ScreenId } from '../../shared';

export type KeyScope = 'global' | ScreenId | `overlay:${string}`;
export interface KeyBinding { scope: KeyScope; key: string; action: string }

/** Screens whose digits are actions (tabs are on `Alt+1…5` there). */
export const DIGIT_ACTION_SCREENS: readonly ScreenId[] = ['combat/fight', 'cards/match'];

const RESERVED_GLOBAL = ['m', '?', 'Escape', '1', '2', '3', '4', '5', 'Alt+1', 'Alt+2', 'Alt+3', 'Alt+4', 'Alt+5'];

const b = (scope: KeyScope, key: string, action: string): KeyBinding => ({ scope, key, action });
const digits = (scope: KeyScope, from: number, to: number, action: (i: number) => string, mod = ''): KeyBinding[] =>
  Array.from({ length: to - from + 1 }, (_, i) => b(scope, `${mod}${(from + i) % 10}`, action(from + i)));

export const KEYMAP: readonly KeyBinding[] = [
  // ── global ──
  ...digits('global', 1, 5, (i) => `tab.${['life', 'road', 'arena', 'cards', 'order'][i - 1]}`),
  ...digits('global', 1, 5, (i) => `tab.${['life', 'road', 'arena', 'cards', 'order'][i - 1]}`, 'Alt+'),
  b('global', 'Escape', 'back'),
  b('global', '?', 'shortcuts'),
  b('global', 'm', 'mute'),
  // ── Житие ──
  b('meta/life', 'Enter', 'life.main'),
  b('meta/life', 'd', 'life.day'),
  ...digits('meta/life', 1, 6, (i) => `life.section.${i}`, 'Shift+'),
  // ── Поединок (Esc = pause, R26) ──
  ...digits('combat/fight', 1, 3, (i) => `fight.prayer.${i}`),
  b('combat/fight', ' ', 'fight.omen'),
  b('combat/fight', 'a', 'fight.auto'),
  b('combat/fight', 'q', 'fight.pets'),
  b('combat/fight', 'x', 'fight.notNow'),
  b('combat/fight', 'f', 'fight.speed'),
  b('combat/fight', 's', 'fight.skip'),
  b('combat/fight', 'p', 'fight.pause'),
  b('combat/fight', 'b', 'fight.bless'),
  b('combat/fight', 'v', 'fight.smite'),
  // ── Странствия (fork options live on the fork sheet) ──
  b('explore/road', 'b', 'road.bless'),
  b('explore/road', 's', 'road.smite'),
  b('explore/road', 'w', 'road.whisper'),
  b('explore/road', 'x', 'road.miracle'),
  b('explore/road', 'c', 'road.codex'),
  b('explore/road', 't', 'road.town'),
  b('explore/road', 'j', 'road.filter'),
  b('explore/road', 'r', 'road.path'),
  b('explore/road', 'd', 'road.doodle'),
  b('explore/road', 'g', 'road.goal'),
  ...digits('overlay:fork', 1, 3, (i) => `fork.option.${i}`),
  ...digits('overlay:fork', 1, 3, (i) => `fork.nudge.${i}`, 'Shift+'),
  b('overlay:fork', ' ', 'fork.letHim'),
  // ── Облик ──
  b('creature/make', 'ArrowLeft', 'make.prev'),
  b('creature/make', 'ArrowRight', 'make.next'),
  b('creature/make', 'ArrowUp', 'make.stagePrev'),
  b('creature/make', 'ArrowDown', 'make.stageNext'),
  b('creature/make', 'a', 'make.rotateLeft'),
  b('creature/make', 'd', 'make.rotateRight'),
  b('creature/make', 'z', 'make.zoom'),
  b('creature/make', 'c', 'make.compare'),
  b('creature/make', 'r', 'make.random'),
  b('creature/make', 'v', 'make.view'),
  b('creature/make', 'Enter', 'make.done'),
  // ── card match ──
  ...digits('cards/match', 1, 10, (i) => `match.card.${i}`),
  b('cards/match', 'q', 'match.row.1'),
  b('cards/match', 'w', 'match.row.2'),
  b('cards/match', 'e', 'match.row.3'),
  b('cards/match', 'Enter', 'match.play'),
  b('cards/match', ' ', 'match.inspect'),
  b('cards/match', 'p', 'match.passHold'),
  b('cards/match', 'h', 'match.hand'),
];

/** Normalises a keyboard event to the table's key names: `Alt+1`, `Shift+2`, `Escape`, `?`, `m`, ` `. */
export function keyOf(e: Pick<KeyboardEvent, 'key' | 'code' | 'altKey' | 'shiftKey' | 'ctrlKey' | 'metaKey'>): string {
  if (e.ctrlKey || e.metaKey) return '';
  const digit = /^Digit(\d)$/.exec(e.code)?.[1] ?? /^Numpad(\d)$/.exec(e.code)?.[1];
  if (digit !== undefined) return `${e.altKey ? 'Alt+' : ''}${e.shiftKey ? 'Shift+' : ''}${digit}`;
  if (e.key === '?') return '?';
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  const latin = /^Key([A-Z])$/.exec(e.code)?.[1]?.toLowerCase();   // layout-independent letters (ЙЦУКЕН keyboards)
  return `${e.altKey ? 'Alt+' : ''}${latin ?? k}`;
}

/** Resolves a key against the active scopes (innermost first: overlay, screen, then global). */
export function resolveKey(key: string, scopes: readonly KeyScope[], map: readonly KeyBinding[] = KEYMAP): KeyBinding | null {
  const screen = scopes.find((s) => s !== 'global' && !s.startsWith('overlay:'));
  for (const scope of [...scopes.filter((s) => s.startsWith('overlay:')), ...(screen ? [screen] : []), 'global' as const]) {
    if (scope === 'global' && screen && (DIGIT_ACTION_SCREENS as readonly string[]).includes(screen) && /^\d$/.test(key)) continue;
    const hit = map.find((x) => x.scope === scope && x.key === key);
    if (hit) return hit;
  }
  return null;
}

/** Duplicate `(scope, key)` pairs and screen bindings that shadow a reserved global key (A38). [] = valid. */
export function validateKeymap(map: readonly KeyBinding[] = KEYMAP): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();
  for (const k of map) {
    const id = `${k.scope}|${k.key}`;
    if (seen.has(id)) errors.push(`duplicate binding ${id}`);
    seen.add(id);
    if (k.scope === 'global' || k.scope.startsWith('overlay:')) continue;
    if (!RESERVED_GLOBAL.includes(k.key)) continue;
    const digitException = /^\d$/.test(k.key) && (DIGIT_ACTION_SCREENS as readonly string[]).includes(k.scope);
    if (!digitException) errors.push(`${k.scope} binds reserved global key "${k.key}" (${k.action})`);
  }
  if (map.some((k) => k.action.includes('miracle') && k.key === 'm')) errors.push('Miracle must be X, not M (R32)');
  return errors;
}
